import React, { useState } from 'react'

import { Header } from '@driftt/dds'
import { Button, Div } from '@driftt/tide-core'
import { Icon } from '@driftt/tide-icons'
import { Select } from '@driftt/tide-selects'
import { access } from '@driftt/traffic-control'

import Card from 'components/Card'

import { ReactComponent as SalesforceIcon } from '../../../svgs/salesforce.svg'

import styles from './styles.module.css'

interface Props {
  baseUrl: string
  accounts: any
  opportunities: any
}

interface LinkProps {
  objectId: string
  baseUrl: string
}

const formatCurrency = (value: number) => {
  return value?.toLocaleString('en-us', {
    style: 'currency',
    currency: 'USD'
  })
}

const formatOptions = (data: any) => data.map((opp: any) => ({ label: opp.Name, value: opp }))

const SalesforceLink = (props: LinkProps) => {
  const { objectId, baseUrl } = props

  const baseUrlWithSlash = !baseUrl.endsWith('/') ? `${baseUrl}/` : baseUrl
  const fullUrl = `${baseUrlWithSlash}${objectId}`

  return (
    <a href={fullUrl} target="_blank" rel="noopener noreferrer" className={styles.salesforceLink}>
      Open in Salesforce <Icon name="open-new-tab" width={12} height={12} />
    </a>
  )
}

const selectDefault = (opportunities: any) => {
  if (opportunities?.length > 0) {
    return opportunities.find((opp: any) => !opp?.IsClosed) || opportunities[0]
  }
  return null
}

const SalesforceSection = (props: Props) => {
  const { baseUrl, accounts = [], opportunities = [] } = props
  const isAdmin = access.isAdmin()
  const [isOpen, setIsOpen] = useState(true)
  const [opportunity, setOpportunity] = useState(selectDefault(opportunities))

  const account = accounts?.[0]

  return (
    <Card>
      <Div className={styles.sectionHeader}>
        <Div className={styles.iconHeader}>
          <SalesforceIcon className={styles.sectionAvatar} />
          <Header variant="h4">Salesforce</Header>
        </Div>
        <Div className={styles.collapseIcon} onClick={() => setIsOpen(!isOpen)}>
          <Icon name={isOpen ? 'chevron-down' : 'chevron-up'} width={12} />
        </Div>
      </Div>
      {isOpen && (
        <Div className={styles.sectionContent}>
          {account && (
            <Div>
              <Div className={styles.subHeader}>
                <Div>Account</Div>
                {account?.Id && <SalesforceLink objectId={account?.Id} baseUrl={baseUrl} />}
              </Div>
              <Div className={styles.field}>
                <Div className={styles.fieldLabel}>Account owner</Div>
                <Div>{account?.Owner?.Name || '−'}</Div>
              </Div>
              <Div className={styles.field}>
                <Div className={styles.fieldLabel}>Account name</Div>
                <Div>{account?.Name || '−'}</Div>
              </Div>
              <Div className={styles.field}>
                <Div className={styles.fieldLabel}>Account type</Div>
                <Div>{account?.type || '-'}</Div>
              </Div>
            </Div>
          )}
          {opportunity && (
            <Div>
              <Div className={styles.subHeader}>
                <Div>Opportunity</Div>
                {opportunity?.Id && <SalesforceLink objectId={opportunity?.Id} baseUrl={baseUrl} />}
              </Div>
              <Div className={styles.field}>
                <Div className={styles.fieldLabel}>Opportunity name</Div>
                {opportunities.length > 0 ? (
                  <Select
                    options={formatOptions(opportunities)}
                    onChange={(option: any) => setOpportunity(option?.value)}
                    value={{ label: opportunity.Name, value: opportunity }}
                  />
                ) : (
                  <Div>{opportunity?.Name || '−'}</Div>
                )}
              </Div>
              <Div className={styles.field}>
                <Div className={styles.fieldLabel}>Opportunity stage</Div>
                <Div>{opportunity?.StageName || '-'}</Div>
              </Div>
              <Div className={styles.field}>
                <Div className={styles.fieldLabel}>Amount</Div>
                <Div>{formatCurrency(opportunity?.Amount) || '-'}</Div>
              </Div>
            </Div>
          )}
          {isAdmin && (
            <Button
              className={styles.cardButton}
              type="tertiary"
              href={'/contacts/contact-settings'}
              target="_blank">
              Manage fields <Icon name="open-new-tab" width={13} height={13} />
            </Button>
          )}
        </Div>
      )}
    </Card>
  )
}

export default SalesforceSection
